import { ID } from '@adornis/baseql/baseqlTypes.js';
import { registerMutation, registerQuery } from '@adornis/baseql/metadata/register.js';
import { getCollection, getRawCollection } from '@adornis/baseql/server/collections.js';
import type { BaseQLSelectionSet, NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import { Timeframe } from '../entities/Timeframe.js';

const registerGetActiveTimeframesByInstitutionID = registerQuery({
  operationName: 'getActiveTimeframesByInstitutionID',
  resolve:
    (institutionID: string) =>
    async <SelSet extends BaseQLSelectionSet<Timeframe>>(
      gqlFields: SelSet,
    ): Promise<NarrowedEntity<Timeframe, SelSet>[]> => {
      const collection = await getCollection(Timeframe);
      const result = await collection.find<Timeframe>({ institutionID, isActive: true }).toArray();
      return result as unknown as NarrowedEntity<Timeframe, SelSet>[];
    },
  type: () => [Timeframe],
  params: [{ name: 'institutionID', type: () => ID }],
});

const activateTimeframe = registerMutation({
  operationName: 'activateTimeframe',
  resolve: (timeframeID: string) => async () => {
    const collection = await getCollection(Timeframe._class);
    const timeframe = await collection.findOne<Timeframe>({ _id: timeframeID });
    if (!timeframe) throw new Error(`Timeframe with ID ${timeframeID} not found`);
    timeframe.isActive = true;
    const updateResult = await collection.updateOne({ _id: timeframeID }, { $set: { isActive: true } });
    if (updateResult.modifiedCount) {
      const rawCollection = await getRawCollection(Timeframe._collectionName);
      await rawCollection.updateMany({ _id: { $ne: timeframeID } }, { $set: { isActive: false } });
      return true;
    }
    return false;
  },
  type: () => Boolean,
  params: [{ name: 'timeframe', type: () => ID }],
});

export namespace TimeframeAPI {
  export const getActiveTimeframesByInstitution = registerGetActiveTimeframesByInstitutionID;
  export const activate = activateTimeframe;
}
